import type ApiBathCard from 'src/core/models/api/ApiBathCard';
import type ApiWorkspace from 'src/core/models/api/ApiWorkspace';
import { OneTimePasswordType } from 'src/core/models/api/responses/ApiCreateOneTimePasswordResponse';
import Constants from 'src/core/utils/Constants';
import Translations from 'src/core/utils/Translations';
import { environmentWebUrl } from 'src/core/utils/WebUrls';
import { useApiSettingsStore } from 'src/domain/stores/useApiSettingsStore';
import { useAuthStore } from 'src/domain/stores/useAuthStore';
import { useGlobalStore } from 'src/domain/stores/useGlobalStore';
import { useLeaveAppStore } from 'src/domain/stores/useLeaveAppStore';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import { useWorkspacesStore } from 'src/domain/stores/useWorkspacesStore';

// The home screen, the bath card tab and the resort picker all send a member off with the same card,
// so the routes out of it live here instead of in each of them.
export const useBathCardActions = () => {
	const { apiSettings } = useApiSettingsStore();
	const { postApiCreateOneTimePassword } = useAuthStore();
	const { getGaOriginUrl } = useGlobalStore();
	const { confirmLeaving } = useLeaveAppStore();
	const { captureEvent } = useServicesStore();
	const { openResortPicker } = useWorkspacesStore();

	// Buying starts at the resort picker: the card a member already holds says nothing about the
	// resort they want the next one for.
	const openBathCardPurchase = () => {
		if (apiSettings?.bath_card_resort_screen_feature_enabled) {
			return openResortPicker();
		}

		confirmLeaving({
			url: getGaOriginUrl({ url: environmentWebUrl(Translations.bathcardUrl) }),
			description: Translations.bathcardLeaveAppDescription,
		});
	};

	const openBathCardOrder = async (workspace: ApiWorkspace) => {
		await postApiCreateOneTimePassword({
			type: OneTimePasswordType.MemberInfo,
			onSuccess: (response) => {
				const url = `${workspace.bath_card_order_page_url}?otp=${response.data.urlToken}`;

				return confirmLeaving({ url, description: Translations.bathcardLeaveAppDescription });
			},
			onError: (error) => captureEvent({ message: 'postApiCreateOneTimePassword failed', extra: { error } }),
		});
	};

	// A resort the app does not carry in this language keeps its own website for the whole flow.
	const openWorkspaceWebsite = (workspace: ApiWorkspace) =>
		confirmLeaving({ url: workspace.pre_reservation_flow_url, description: Translations.leaveAppDescription });

	const openReservationWithBathCard = (card: ApiBathCard) => {
		const url = card.reservation_flow_url;

		if (url === null || url.length === 0) {
			captureEvent({ message: 'Reserving with a bath card without a reservation flow url', extra: { cardId: card.id } });

			return;
		}

		return confirmLeaving({
			url,
			description: Translations.reserveLeaveAppDescription,
			webMessage: Constants.bridgeMessageReservationFlowOpen,
		});
	};

	return { openBathCardOrder, openBathCardPurchase, openReservationWithBathCard, openWorkspaceWebsite };
};

export default useBathCardActions;
